import type { ErrorHandler } from '@angular/core';
import { inject, Injectable, NgZone } from '@angular/core';
import { AppError } from '@core/error-handler/models/error.model';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorMappingRegistryService } from './error-mappings/error-mapping-registry.service';
import { NotificationService } from '@core/notification-handler/services/notification.service';

const TOAST_DURATION = 10000;

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService implements ErrorHandler {
  private zone = inject(NgZone);
  private errorMappingRegistry = inject(ErrorMappingRegistryService);
  private notificationService = inject(NotificationService);

  async handleError(error: unknown) {
    // Keep the raw error visible to debugging tools - the toast below only
    // surfaces a short message for 10s and otherwise swallows the stack.
    console.error(error);

    let message: string;
    let duration = TOAST_DURATION;

    await this.zone.run(async () => {
      if (error && typeof error === 'object' && '_handledByInterceptor' in error) {
        return;
      }

      if (error instanceof AppError) {
        message = error.message;
        duration = error.duration || duration;
      } else if (error instanceof Error) {
        message = error.message;
      } else if (typeof error === 'string') {
        message = error;
      } else if (error instanceof HttpErrorResponse) {
        // Use the error mapping registry for HTTP errors
        const appError = this.errorMappingRegistry.getErrorForRequest(
          error.url || '',
          error.status
        );
        message = appError.message;
        duration = appError.duration || duration;
      } else {
        message = 'An unknown error occurred';
      }

      await this.notificationService.showInfo(message, duration);
    });
  }
}
